import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import {
    cellValue,
    compareSeoRows,
    fmt,
    fmtDelta,
    heatBackground,
    metricDigits,
    rowMax,
    windowValue,
    type SeoSort,
} from './format';
import type { SeoBlock, SeoCell, SeoGroup, SeoMetric, SeoReport, SeoRow, SeoScale, SeoWeek } from './types';

const METRICS: { id: SeoMetric; label: string }[] = [
    { id: 'position', label: 'Posición' },
    { id: 'impressions', label: 'Impresiones' },
    { id: 'clicks', label: 'Clics' },
];

const BANDS: [number, string][] = [
    [8, '1–3'],
    [7, '3–5'],
    [6, '5–8'],
    [5, '8–10'],
    [4, '10–15 · fuera de la página 1'],
    [3, '15–20'],
    [2, '20–30'],
    [1, 'más de 30'],
];

function Chip({
    pressed,
    onClick,
    children,
}: {
    pressed?: boolean;
    onClick: () => void;
    children: string;
}) {
    return (
        <button
            type="button"
            aria-pressed={pressed}
            onClick={onClick}
            className={cn(
                'rounded-full border px-3 py-1 text-sm',
                pressed
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-card text-foreground hover:bg-muted/60',
            )}
        >
            {children}
        </button>
    );
}

function columnLabel(id: string, grain: SeoReport['grain']): string {
    if (grain !== 'day') return id.slice(5);
    const parts = id.split('-');
    return parts.length === 3 ? `${parts[2]}/${parts[1]}` : id;
}

function cellTitle(week: SeoWeek | undefined, cell: SeoCell): string {
    return [
        week ? `${week.id} · ${week.start} → ${week.end}` : '',
        `Clics ${fmt(cell.clicks, 0)}`,
        `Impresiones ${fmt(cell.impressions, 0)}`,
        `CTR ${fmt(cell.ctr, 2)} %`,
        `Posición ${fmt(cell.position, 1)}${cell.unreliable ? ' · poco fiable' : ''}`,
    ].filter(Boolean).join('\n');
}

export function SeoHeatmap({ report }: { report: SeoReport }) {
    const [metric, setMetric] = useState<SeoMetric>('position');
    const [scale, setScale] = useState<SeoScale>('row');
    const [query, setQuery] = useState('');
    const [blockId, setBlockId] = useState('all');
    const [sort, setSort] = useState<SeoSort>('desc');
    const [open, setOpen] = useState<Record<string, boolean>>({});
    const digits = metricDigits(metric);
    const needle = query.trim().toLowerCase();

    const blocks = useMemo(() => {
        const visible = report.blocks
            .filter((block) => blockId === 'all' || block.id === blockId)
            .map((block) => sortBlock(filterBlock(block, needle), metric, sort))
            .filter((block) => blockId !== 'all' || !needle || block.rows.length > 0 || (block.groups?.length ?? 0) > 0);
        if (sort === 'catalog') return visible;
        return [...visible].sort((a, b) => compareSeoRows(a.summary, b.summary, metric, sort));
    }, [report.blocks, blockId, needle, metric, sort]);

    const commonMax = useMemo(() => {
        let max = 0;
        for (const block of report.blocks) {
            for (const row of block.rows) {
                max = Math.max(max, rowMax(row.cells, metric));
            }
        }
        return max;
    }, [report.blocks, metric]);

    const allOpen = blocks.every((block) => open[block.id]);

    return (
        <section className="space-y-3" style={{ ['--seo-heat' as string]: report.colors.petrol }}>
            <div className="flex flex-wrap items-center gap-2">
                {METRICS.map((item) => (
                    <Chip key={item.id} pressed={metric === item.id} onClick={() => setMetric(item.id)}>
                        {item.label}
                    </Chip>
                ))}
                {metric !== 'position' && (
                    <>
                        <Chip pressed={scale === 'row'} onClick={() => setScale('row')}>Por página</Chip>
                        <Chip pressed={scale === 'common'} onClick={() => setScale('common')}>Común</Chip>
                    </>
                )}
                <Chip
                    pressed={allOpen}
                    onClick={() => {
                        const next = !allOpen;
                        setOpen(Object.fromEntries(blocks.map((block) => [block.id, next])));
                    }}
                >
                    {allOpen ? 'Plegar bloques' : 'Abrir bloques'}
                </Chip>
            </div>
            <div className="flex flex-wrap gap-2">
                <select
                    aria-label="Bloque"
                    value={blockId}
                    onChange={(event) => setBlockId(event.target.value)}
                    className="rounded-lg border border-border bg-card px-3 py-1.5 text-sm"
                >
                    <option value="all">Todos los bloques</option>
                    {report.blocks.map((block) => (
                        <option key={block.id} value={block.id}>{block.name}</option>
                    ))}
                </select>
                <select
                    aria-label="Orden"
                    value={sort}
                    onChange={(event) => setSort(event.target.value as SeoSort)}
                    className="rounded-lg border border-border bg-card px-3 py-1.5 text-sm"
                >
                    <option value="desc">Mayor a menor</option>
                    <option value="asc">Menor a mayor</option>
                    <option value="catalog">Orden del catálogo</option>
                </select>
                <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Filtrar página"
                    aria-label="Filtrar página"
                    className="min-w-[12rem] flex-1 rounded-lg border border-border bg-card px-3 py-1.5 text-sm"
                />
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                {metric === 'position'
                    ? BANDS.map(([step, label]) => (
                        <span key={label} className="inline-flex items-center gap-1">
                            <i
                                className="inline-block h-3.5 w-3.5 rounded-sm"
                                style={{ background: `color-mix(in srgb, var(--seo-heat) ${Math.round((step / 8) * 100)}%, hsl(var(--card)))` }}
                            />
                            {label}
                        </span>
                    ))
                    : <span>{metric === 'clicks' ? 'Más oscuro: más clics.' : 'Más oscuro: más impresiones.'}</span>}
                <span>Poco fiable: menos de 20 impresiones.</span>
                {sort !== 'catalog' && (
                    <span>
                        Orden por {METRICS.find((item) => item.id === metric)?.label.toLowerCase()} de «{report.compare.after_label}».
                        {metric === 'position' ? ' En posición, un número mayor está más lejos del primer resultado.' : ''}
                    </span>
                )}
            </div>
            <div className="max-w-full overflow-x-auto rounded-xl border border-border bg-card">
                <table className="min-w-[720px] border-separate border-spacing-0 text-xs">
                    <thead>
                        <tr>
                            <th className="sticky left-0 z-20 bg-card px-2 py-2 text-left font-semibold">Página</th>
                            {report.weeks.map((week) => (
                                <th key={week.id} className="px-2 py-2 text-center font-semibold whitespace-nowrap">
                                    {columnLabel(week.id, report.grain)}
                                </th>
                            ))}
                            <th className="px-2 py-2 text-right font-semibold">{report.compare.before_label}</th>
                            <th className="px-2 py-2 text-right font-semibold" title={sort === 'catalog' ? undefined : 'Esta columna ordena bloques, grupos y páginas'}>
                                {report.compare.after_label}
                            </th>
                            <th className="px-2 py-2 text-right font-semibold">Cambio</th>
                        </tr>
                    </thead>
                    <tbody>
                        {blocks.map((block) => (
                            <BlockRows
                                key={block.id}
                                block={block}
                                weeks={report.weeks}
                                metric={metric}
                                scale={scale}
                                digits={digits}
                                commonMax={commonMax}
                                expanded={Boolean(open[block.id]) || Boolean(needle)}
                                forceGroups={Boolean(needle)}
                                open={open}
                                onToggle={() => setOpen((current) => ({ ...current, [block.id]: !current[block.id] }))}
                                onToggleGroup={(groupId) => setOpen((current) => {
                                    const key = `${block.id}/${groupId}`;
                                    return { ...current, [key]: !current[key] };
                                })}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function sortBlock(block: SeoBlock, metric: SeoMetric, sort: SeoSort): SeoBlock {
    if (sort === 'catalog') return block;
    return {
        ...block,
        rows: [...block.rows].sort((a, b) => compareSeoRows(a, b, metric, sort)),
        groups: block.groups
            ?.map((group) => ({
                ...group,
                rows: [...group.rows].sort((a, b) => compareSeoRows(a, b, metric, sort)),
            }))
            .sort((a, b) => compareSeoRows(a.summary, b.summary, metric, sort)),
    };
}

function filterBlock(block: SeoBlock, needle: string): SeoBlock {
    if (!needle) return block;
    const match = (row: SeoRow) => `${row.label} ${row.path ?? ''}`.toLowerCase().includes(needle);
    const groups = block.groups
        ?.map((group) => ({ ...group, rows: group.rows.filter(match) }))
        .filter((group) => group.rows.length > 0);
    return {
        ...block,
        rows: block.rows.filter(match),
        groups,
    };
}

function BlockRows({
    block,
    weeks,
    metric,
    scale,
    digits,
    commonMax,
    expanded,
    forceGroups,
    open,
    onToggle,
    onToggleGroup,
}: {
    block: SeoBlock;
    weeks: SeoWeek[];
    metric: SeoMetric;
    scale: SeoScale;
    digits: number;
    commonMax: number;
    expanded: boolean;
    forceGroups: boolean;
    open: Record<string, boolean>;
    onToggle: () => void;
    onToggleGroup: (groupId: string) => void;
}) {
    return (
        <>
            <HeatRow
                row={block.summary}
                label={block.name}
                weeks={weeks}
                metric={metric}
                scale={scale}
                digits={digits}
                commonMax={commonMax}
                header
                expanded={expanded}
                onToggle={onToggle}
            />
            {expanded && block.groups?.length
                ? block.groups.map((group) => (
                    <GroupRows
                        key={group.id}
                        group={group}
                        weeks={weeks}
                        metric={metric}
                        scale={scale}
                        digits={digits}
                        commonMax={commonMax}
                        expanded={Boolean(open[`${block.id}/${group.id}`]) || forceGroups}
                        onToggle={() => onToggleGroup(group.id)}
                    />
                ))
                : expanded && block.rows.map((row, index) => (
                    <PageRow key={`${row.path || 'resto'}-${index}`} row={row} weeks={weeks} metric={metric} scale={scale} digits={digits} commonMax={commonMax} />
                ))}
        </>
    );
}

function GroupRows({
    group,
    weeks,
    metric,
    scale,
    digits,
    commonMax,
    expanded,
    onToggle,
}: {
    group: SeoGroup;
    weeks: SeoWeek[];
    metric: SeoMetric;
    scale: SeoScale;
    digits: number;
    commonMax: number;
    expanded: boolean;
    onToggle: () => void;
}) {
    return (
        <>
            <HeatRow
                row={group.summary}
                label={group.label}
                weeks={weeks}
                metric={metric}
                scale={scale}
                digits={digits}
                commonMax={commonMax}
                indent="group"
                expanded={expanded}
                onToggle={onToggle}
            />
            {expanded && group.rows.map((row, index) => (
                <PageRow
                    key={`${row.path || 'resto'}-${index}`}
                    row={row}
                    weeks={weeks}
                    metric={metric}
                    scale={scale}
                    digits={digits}
                    commonMax={commonMax}
                    indent
                />
            ))}
        </>
    );
}

function PageRow({
    row,
    weeks,
    metric,
    scale,
    digits,
    commonMax,
    indent,
}: {
    row: SeoRow;
    weeks: SeoWeek[];
    metric: SeoMetric;
    scale: SeoScale;
    digits: number;
    commonMax: number;
    indent?: boolean;
}) {
    return (
        <HeatRow
            row={row}
            label={row.resto ? `${row.label} (${row.resto_count})` : row.label}
            title={row.path || undefined}
            weeks={weeks}
            metric={metric}
            scale={scale}
            digits={digits}
            commonMax={commonMax}
            indent={indent ? 'page' : undefined}
        />
    );
}

function HeatRow({
    row,
    label,
    title,
    weeks,
    metric,
    scale,
    digits,
    commonMax,
    header,
    indent,
    expanded,
    onToggle,
}: {
    row: SeoRow;
    label: string;
    title?: string;
    weeks: SeoWeek[];
    metric: SeoMetric;
    scale: SeoScale;
    digits: number;
    commonMax: number;
    header?: boolean;
    indent?: 'group' | 'page';
    expanded?: boolean;
    onToggle?: () => void;
}) {
    const max = rowMax(row.cells, metric);
    return (
        <tr className={header ? 'bg-primary/10 font-semibold' : undefined}>
            <th className={cn(
                'sticky left-0 z-10 px-2 py-1.5 text-left font-medium max-w-[16rem]',
                header ? 'bg-primary/10' : 'bg-card',
                indent === 'group' && 'pl-6 font-semibold',
                indent === 'page' && 'pl-10',
            )}>
                {onToggle && (
                    <button type="button" onClick={onToggle} className="mr-1 font-semibold" aria-label={expanded ? 'Plegar' : 'Abrir'}>
                        {expanded ? '−' : '+'}
                    </button>
                )}
                <span title={title}>{label}</span>
            </th>
            {row.cells.map((cell, index) => (
                <td
                    key={weeks[index]?.id ?? index}
                    title={cell ? cellTitle(weeks[index], cell) : undefined}
                    className={cn(
                        'px-2 py-1.5 text-right tabular-nums whitespace-nowrap',
                        metric === 'position' && cell?.unreliable && 'outline outline-1 outline-dashed outline-muted-foreground/70',
                    )}
                    style={{ background: heatBackground(metric, cell, max, commonMax, scale) }}
                >
                    {fmt(cellValue(cell, metric), digits)}
                </td>
            ))}
            <td className="px-2 py-1.5 text-right tabular-nums">{fmt(windowValue(row.before, metric), digits)}</td>
            <td className="px-2 py-1.5 text-right tabular-nums">{fmt(windowValue(row.after, metric), digits)}</td>
            <td className="px-2 py-1.5 text-right tabular-nums">{fmtDelta(row.change?.[metric] ?? null, digits)}</td>
        </tr>
    );
}
